import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAt, faEnvelope, faLocationDot, faPhone } from "@fortawesome/free-solid-svg-icons";
import { faFacebookF, faInstagram, faLinkedinIn, faWhatsapp } from "@fortawesome/free-brands-svg-icons";

import SEO, { pageSEO } from "../components/seo.component";
import constants from "../constants/constants";
import { fadeUp } from "../functions/animations";
import { useUserParams } from "../hooks/useUserParams";

export default function About() {
  const {
    getCurrentAgentName,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentAbout,
    getCurrentHeadshot,
  } = useUserParams();
  const seoConfig = pageSEO.about();

  return (
    <>
      <SEO {...seoConfig} />

      {/* HERO */}
      <section className="relative h-[90vh] overflow-hidden">
        <img
          src={getCurrentHeadshot()}
          alt={getCurrentAgentName()}
          className="absolute inset-0 h-full w-full object-cover"
        />

        <div className="absolute inset-0 bg-black/50" />

        <div className="relative z-10 flex h-full items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .9 }}
            className="text-white"
          >
            <p className="mb-8 text-sm uppercase tracking-[0.6em] text-white/70">
              About Me
            </p>

            <h1 className="font-heading2 text-5xl sm:text-6xl lg:text-8xl leading-none">
              {getCurrentAgentName()}
            </h1>

            <p className="mx-auto mt-8 max-w-2xl text-lg leading-8 text-white/80">
              Trusted guidance, thoughtful strategy,
              and exceptional real estate experiences.
            </p>

            <Link
              to="/contact"
              className="btn-primary-outline mt-10 inline-block"
            >
              Let's Connect
            </Link>
          </motion.div>
        </div>

        <h2
          className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 whitespace-nowrap font-serif text-[100px] uppercase tracking-[0.3em] text-white/10 lg:text-[220px]"
        >
          About
        </h2>

        {/* Contact Bar */}
        <div
          className="absolute bottom-10 left-1/2 z-20 w-full -translate-x-1/2 px-6"
        >
          <div
            className="mx-auto flex max-w-[1200px] flex-col items-center justify-between gap-6 border-t border-white/20 pt-8 text-white md:flex-row"
          >
            {/* Phone */}
            <a
              href={`tel:${getCurrentAgentNumber()}`}
              className="flex items-center gap-4 text-sm uppercase tracking-[0.25em] text-white/80 transition hover:text-white"
            >
              <FontAwesomeIcon
                icon={faPhone}
                className="text-white/60"
              />
              <span>
                {getCurrentAgentNumber()}
              </span>
            </a>

            {/* Email */}
            <a
              href={`mailto:${getCurrentAgentEmail()}`}
              className="flex items-center gap-4 text-sm uppercase tracking-[0.25em] text-white/80 transition hover:text-white"
            >
              <FontAwesomeIcon
                icon={faEnvelope}
                className="text-white/60"
              />
              <span>
                {getCurrentAgentEmail()}
              </span>
            </a>

            {/* Social */}
            <div
              className="flex items-center gap-5"
            >
              {Object.entries(constants.SOCIALS)?.map(([k, v]) => (
                <a
                  href={constants.SOCIALS[k]}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 text-white/80 transition hover:bg-white hover:text-black"
                  target="_blank"
                >
                  <FontAwesomeIcon icon={constants.ICONS[k]} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="bg-light py-24 lg:py-40">
        <div className="mx-auto grid max-w-[1300px] grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-12">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="lg:col-span-5"
          >
            <p className="mb-8 text-sm uppercase tracking-[0.6em] text-black/40">
              My Story
            </p>

            <h2 className="font-heading2 text-4xl leading-tight text-neutral-800 sm:text-5xl lg:text-6xl">
              More Than
              <br />
              A Real Estate Agent
            </h2>
            <div className="mt-10 h-px w-24 bg-accent" />
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="lg:col-span-7 text-gray-600 leading-9"
          >
            <p className="whitespace-pre-line">
              {getCurrentAbout()}
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}